import { useState } from 'react';
import { X, Search, BookOpen, CheckSquare } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { Spinner } from './Spinner';
import type { Remark } from '@/types/api';

interface Props {
  open: boolean;
  projectId: string;
  remark: Remark | null;
  onClose: () => void;
}

interface MatcherResult {
  ranked?: Array<{ remark_id: string; score: number; body?: string; severity?: string; section_code?: string; status?: string }>;
  scores?: Array<{ remark_id: string; score: number }>;
}

interface ArgueResult {
  argumentation?: string;
  cited_norms?: string[];
  confidence?: number;
  stub?: boolean;
  note?: string;
}

interface CrossCheckRequirement {
  chunk_id: string;
  norm_code: string | null;
  section_number: string | null;
  requirement_text: string;
  compliance_status: 'ok' | 'gap' | 'partial' | 'unknown';
  compliance_note: string;
  evidence: Array<{ chunk_id: string; text: string; page?: number | null; title?: string | null; distance?: number }>;
}

interface CrossCheckResult {
  query: string;
  project_id: string;
  requirements: CrossCheckRequirement[];
  stub?: boolean;
  note?: string;
}

type DrawerTab = 'similar' | 'argue' | 'crosscheck';

/** Side-drawer «Похожие замечания» + «Аргументация позиции» + «Проверка норм». */
export function MatcherDrawer({ open, projectId, remark, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<DrawerTab>('similar');
  const [crossCheckQuery, setCrossCheckQuery] = useState('');
  const [crossCheckTrigger, setCrossCheckTrigger] = useState(0);

  // Tab 1: similar remarks
  const { data: matcherData, isLoading: matcherLoading, isError: matcherError } = useQuery({
    queryKey: ['matcher', projectId, remark?.id],
    queryFn: async () => {
      if (!remark) return null;
      return api.post<MatcherResult>(`/projects/${projectId}/snapshots/${remark.snapshot_id}/matcher/rank-candidate`, {
        candidate_text: remark.body,
        top_k: 5,
      });
    },
    enabled: open && !!remark && activeTab === 'similar',
    retry: false,
  });

  // Tab 2: argumentation
  const { data: argueData, isLoading: argueLoading, isError: argueError, refetch: argueRefetch } = useQuery({
    queryKey: ['argue', projectId, remark?.id],
    queryFn: async () => {
      if (!remark) return null;
      return api.post<ArgueResult>(`/projects/${projectId}/remarks/${remark.id}/norms/argue`, {});
    },
    enabled: open && !!remark && activeTab === 'argue',
    retry: false,
  });

  // Tab 3: cross-check norms
  const crossCheckQueryFinal = crossCheckQuery.trim() || (remark?.body?.slice(0, 200) ?? '');
  const { data: crossCheckData, isLoading: crossCheckLoading, isError: crossCheckError } = useQuery({
    queryKey: ['crosscheck', projectId, remark?.snapshot_id, crossCheckQueryFinal, crossCheckTrigger],
    queryFn: async () => {
      if (!remark || !crossCheckQueryFinal) return null;
      return api.post<CrossCheckResult>(`/projects/${projectId}/norms/cross-check`, {
        query: crossCheckQueryFinal,
        snapshot_id: remark.snapshot_id,
        top_k_norms: 5,
        top_k_project: 2,
      });
    },
    enabled: open && !!remark && activeTab === 'crosscheck' && crossCheckTrigger > 0,
    retry: false,
  });

  if (!open) return null;

  const results = matcherData?.ranked || [];
  const confidence = argueData?.confidence ?? 0;
  const confColor = confidence >= 0.7 ? '#10B981' : confidence >= 0.4 ? '#F59E0B' : '#EF4444';

  return (
    <>
      <div className="fixed inset-0 z-40" style={{ background: 'rgba(10,22,40,.3)' }} onClick={onClose} />
      <aside
        className="fixed top-0 right-0 h-full bg-white border-l border-border flex flex-col z-50"
        style={{ width: 480, maxWidth: '100%', boxShadow: '-16px 0 32px rgba(10,22,40,.12)' }}
      >
        <header className="border-b border-border flex items-start justify-between" style={{ padding: '20px 24px' }}>
          <div style={{ flex: 1 }}>
            <h4 className="font-heading text-ink flex items-center" style={{ fontSize: 18, fontWeight: 600, gap: 8 }}>
              <Search size={16} /> Анализ замечания
            </h4>
            <p className="text-muted mt-1" style={{ fontSize: 12 }}>
              {remark?.section_code || 'без раздела'} · {remark?.id.slice(0, 8)}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-muted" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }} aria-label="Закрыть">
            <X size={20} />
          </button>
        </header>

        {/* Tabs */}
        <div className="flex border-b border-border" style={{ padding: '0 24px' }}>
          {([
            { id: 'similar' as DrawerTab,     label: 'Похожие',      icon: Search },
            { id: 'argue' as DrawerTab,        label: 'Аргументация', icon: BookOpen },
            { id: 'crosscheck' as DrawerTab,   label: 'Проверка норм', icon: CheckSquare },
          ] as const).map(({ id, label, icon: Icon }) => {
            const isActive = activeTab === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActiveTab(id)}
                style={{
                  padding: '10px 14px', fontSize: 13, fontWeight: isActive ? 600 : 500,
                  color: isActive ? '#0A1628' : '#6B7280',
                  background: 'transparent', border: 'none',
                  borderBottom: isActive ? '2px solid #F59E0B' : '2px solid transparent',
                  marginBottom: -1, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <Icon size={13} /> {label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto" style={{ padding: '16px 24px' }}>
          {/* Tab 1: Similar remarks */}
          {activeTab === 'similar' && (
            <>
              {matcherLoading && <div className="text-center" style={{ padding: '32px 0' }}><Spinner size={24} className="text-muted" /></div>}
              {matcherError && (
                <p className="text-muted text-center" style={{ padding: '32px 0', fontSize: 13 }}>
                  Эндпоинт matcher временно недоступен или Chroma не запущен
                </p>
              )}
              {matcherData && results.length === 0 && (
                <p className="text-muted text-center" style={{ padding: '32px 0', fontSize: 13 }}>
                  Похожих замечаний не найдено
                </p>
              )}
              {results.map((r, i) => (
                <div key={r.remark_id || i} className="bg-white rounded-card mb-3" style={{ padding: '12px 16px', border: '1px solid #E5E7EB', cursor: 'pointer' }}>
                  <div className="flex items-center justify-between">
                    <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: severityColor(r.severity) }}>
                      {severityLabel(r.severity)}
                    </span>
                    <span className="rounded-pill tabular" style={{ padding: '2px 10px', fontSize: 12, fontWeight: 600, background: 'rgba(245,158,11,.1)', color: '#B45309' }}>
                      {(r.score || 0).toFixed(2)}
                    </span>
                  </div>
                  {r.body && (
                    <div className="text-ink mt-1.5" style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {r.body}
                    </div>
                  )}
                  <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                    {r.section_code || 'без раздела'} · {r.status || 'статус неизвестен'} · {r.remark_id.slice(0, 8)}
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Tab 2: Argumentation */}
          {activeTab === 'argue' && (
            <>
              {argueLoading && <div className="text-center" style={{ padding: '32px 0' }}><Spinner size={24} className="text-muted" /></div>}
              {argueError && (
                <p className="text-muted text-center" style={{ padding: '32px 0', fontSize: 13 }}>
                  Не удалось сформировать аргументацию
                </p>
              )}
              {argueData && (
                <div>
                  {/* Confidence badge */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em' }}>
                      Позиция проектировщика
                    </span>
                    <span className="rounded-pill tabular" style={{ padding: '2px 10px', fontSize: 11, fontWeight: 700, background: 'rgba(16,185,129,.08)', color: confColor }}>
                      Уверенность: {Math.round(confidence * 100)}%
                    </span>
                  </div>

                  {/* Argumentation text */}
                  <div className="rounded-card" style={{ padding: 16, background: '#F9FAFB', border: '1px solid #E5E7EB', fontSize: 14, lineHeight: 1.6, color: '#0A1628' }}>
                    {argueData.argumentation}
                  </div>

                  {/* Cited norms */}
                  {argueData.cited_norms && argueData.cited_norms.length > 0 && (
                    <div className="mt-4">
                      <div className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 8 }}>
                        Нормативные ссылки
                      </div>
                      <div className="flex" style={{ flexWrap: 'wrap', gap: 6 }}>
                        {argueData.cited_norms.map((norm, i) => (
                          <span key={i} className="rounded-pill" style={{ padding: '3px 10px', fontSize: 12, fontWeight: 500, background: 'rgba(99,102,241,.08)', color: '#4338CA' }}>
                            {norm}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Note / stub warning */}
                  {argueData.stub && (
                    <div className="mt-4 rounded-card" style={{ padding: '10px 14px', background: 'rgba(245,158,11,.06)', border: '1px solid rgba(245,158,11,.3)', fontSize: 12, color: '#92400E' }}>
                      Этап 1: аргументация формируется LLM без верификации по базе нормативов.
                      Следующий шаг — автоматическая проверка цитат в корпусе СП/ГОСТ.
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => argueRefetch()}
                    style={{ marginTop: 16, padding: '8px 16px', fontSize: 12, fontWeight: 600, background: 'transparent', border: '1px solid #E5E7EB', borderRadius: 6, cursor: 'pointer', color: '#6B7280' }}
                  >
                    Сформировать заново
                  </button>
                </div>
              )}
            </>
          )}

          {/* Tab 3: Cross-check */}
          {activeTab === 'crosscheck' && (
            <div>
              <p className="text-muted" style={{ fontSize: 12, marginBottom: 12 }}>
                Двухэтапная проверка: поиск требований в нормативах → сверка с проектной документацией.
              </p>
              <div className="flex" style={{ gap: 8, marginBottom: 16 }}>
                <input
                  type="text"
                  placeholder={`По умолчанию — текст замечания`}
                  value={crossCheckQuery}
                  onChange={(e) => setCrossCheckQuery(e.target.value)}
                  style={{ flex: 1, padding: '7px 12px', fontSize: 13, border: '1px solid #E5E7EB', borderRadius: 6, outline: 'none' }}
                  onKeyDown={(e) => { if (e.key === 'Enter') setCrossCheckTrigger((n) => n + 1); }}
                />
                <button
                  type="button"
                  onClick={() => setCrossCheckTrigger((n) => n + 1)}
                  style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600, background: '#F59E0B', color: '#0A1628', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                >
                  Проверить
                </button>
              </div>

              {crossCheckTrigger === 0 && (
                <p className="text-muted text-center" style={{ padding: '24px 0', fontSize: 13 }}>
                  Нажмите «Проверить» для запуска
                </p>
              )}
              {crossCheckLoading && <div className="text-center" style={{ padding: '32px 0' }}><Spinner size={24} className="text-muted" /></div>}
              {crossCheckError && <p style={{ fontSize: 13, color: '#EF4444', padding: '16px 0' }}>Ошибка — возможно нормативная база не загружена</p>}
              {crossCheckData?.note && (
                <p className="text-muted" style={{ fontSize: 12, padding: '8px 12px', background: '#FEF3C7', borderRadius: 6, marginBottom: 12 }}>
                  {crossCheckData.note}
                </p>
              )}
              {crossCheckData?.requirements && crossCheckData.requirements.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {crossCheckData.requirements.map((req, i) => (
                    <div
                      key={req.chunk_id || i}
                      style={{ padding: '12px 14px', borderRadius: 8, border: `1px solid ${statusBorderColor(req.compliance_status)}`, background: statusBgColor(req.compliance_status) }}
                    >
                      <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: statusTextColor(req.compliance_status) }}>
                          {statusLabel(req.compliance_status)}
                        </span>
                        {req.norm_code && (
                          <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 4, background: 'rgba(99,102,241,.1)', color: '#4338CA', fontWeight: 600 }}>
                            {req.norm_code}{req.section_number ? ` §${req.section_number}` : ''}
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: 13, lineHeight: 1.5, color: '#0A1628', margin: '0 0 4px' }}>
                        {req.requirement_text.slice(0, 250)}{req.requirement_text.length > 250 ? '…' : ''}
                      </p>
                      {req.compliance_note && (
                        <p style={{ fontSize: 12, color: '#6B7280', margin: 0 }}>{req.compliance_note}</p>
                      )}
                      {req.evidence.length > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {req.evidence.slice(0, 2).map((ev, j) => (
                            <div key={j} style={{ fontSize: 11, padding: '4px 8px', background: 'rgba(255,255,255,.7)', borderRadius: 4, color: '#374151' }}>
                              <span style={{ fontWeight: 600 }}>{ev.title || '?'} {ev.page ? `стр.${ev.page}` : ''}: </span>
                              {ev.text.slice(0, 120)}…
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {crossCheckData && crossCheckData.requirements.length === 0 && !crossCheckData.note && (
                <p className="text-muted text-center" style={{ padding: '24px 0', fontSize: 13 }}>Требования не найдены</p>
              )}
            </div>
          )}
        </div>

        <footer className="border-t border-border bg-white flex items-center justify-between" style={{ padding: '12px 24px' }}>
          <span className="text-muted" style={{ fontSize: 11 }}>
            {activeTab === 'similar' && results.length > 0 ? `Показано ${results.length} результата` : ''}
            {activeTab === 'crosscheck' && crossCheckData?.requirements ? `${crossCheckData.requirements.length} требований` : ''}
          </span>
          <span className="text-muted tabular" style={{ fontSize: 11 }}>matcher v2 · argue v1 · crosscheck v1</span>
        </footer>
      </aside>
    </>
  );
}

function severityColor(s?: string): string {
  const v = (s || 'medium').toLowerCase();
  return v === 'critical' ? '#EF4444' : v === 'high' ? '#F59E0B' : v === 'low' ? '#9CA3AF' : '#6B7280';
}
function severityLabel(s?: string): string {
  const v = (s || 'medium').toLowerCase();
  return v === 'critical' ? 'Критическое' : v === 'high' ? 'Высокое' : v === 'low' ? 'Низкое' : 'Среднее';
}

type ComplianceStatus = 'ok' | 'gap' | 'partial' | 'unknown';
function statusLabel(s: ComplianceStatus): string {
  return s === 'ok' ? '✓ Выполнено' : s === 'gap' ? '✗ Не выполнено' : s === 'partial' ? '~ Частично' : '? Недостаточно данных';
}
function statusBorderColor(s: ComplianceStatus): string {
  return s === 'ok' ? '#BBF7D0' : s === 'gap' ? '#FECACA' : s === 'partial' ? '#FDE68A' : '#E5E7EB';
}
function statusBgColor(s: ComplianceStatus): string {
  return s === 'ok' ? '#F0FDF4' : s === 'gap' ? '#FEF2F2' : s === 'partial' ? '#FFFBEB' : '#F9FAFB';
}
function statusTextColor(s: ComplianceStatus): string {
  return s === 'ok' ? '#15803D' : s === 'gap' ? '#B91C1C' : s === 'partial' ? '#92400E' : '#6B7280';
}
